import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Contact, ContactEmailAddress, ContactAddress, Client } from '../../../../models';
import { WorkflowContextService, ClientService, ValidationService } from '../../../../services';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

interface ContactFormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  title: string;
  streetAddress: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  officeHours: string;
}

@Component({
  selector: 'wm-settings-contacts',
  standalone: false,
  templateUrl: './settings-contacts.component.html',
  styleUrls: ['./settings-contacts.component.scss']
})
export class SettingsContactsComponent implements OnInit, OnDestroy {
  tableHeaders: string[] = ['Name', 'Phone', 'Email', 'Actions'];
  tableData: any[] = [];
  
  showNewInline = false;
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  
  currentContactId: string | null = null;
  pendingDeleteIndex: number | null = null;
  
  newContact: ContactFormData = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    title: '',
    streetAddress: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    officeHours: ''
  };

  form: UntypedFormGroup;
  private routerSub$: Subscription;

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };

  constructor(
    private fb: UntypedFormBuilder,
    private _context: WorkflowContextService,
    private _clientSvc: ClientService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: [''],
      phone: ['', [Validators.required, ValidationService.phoneNumberValidator]],
      email: ['', [Validators.required, ValidationService.emailValidator]],
      title: [''],
      streetAddress: [''],
      address2: [''],
      city: [''],
      state: [''],
      zip: [''],
      officeHours: ['']
    });
  }

  ngOnInit() {
    if (history.state && history.state.tableData) {
      this.tableData = history.state.tableData;
    } else {
      this.loadContacts();
    }

    this.syncUrl();
    this.routerSub$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncUrl();
    });
  }

  ngOnDestroy() {
    if (this.routerSub$) {
      this.routerSub$.unsubscribe();
    }
  }

  syncUrl() {
    const urlStr = this.router.url;
    if (urlStr.includes('/contacts/add')) {
      this.setupNewEditor();
    } else if (urlStr.includes('/contacts/edit/')) {
      const id = urlStr.split('/contacts/edit/')[1].split(/[?#]/)[0];
      this.currentContactId = id;
      this.showNewInline = false;
      this.applyExpand();
    } else {
      this.closeAllEditors();
    }
  }

  private mapContactsToTableData(contacts: Contact[]): any[] {
    return contacts.map(contact => {
      let email = '';
      if (contact.emailAddress && typeof contact.emailAddress === 'object') {
        email = (contact.emailAddress as any).email || '';
      }
      
      let address = '';
      if (contact.address && typeof contact.address === 'object') {
        const addr = contact.address as any;
        const parts = [
          addr.address1,
          addr.city,
          addr.state,
          addr.zip
        ].filter(part => part && part.trim() !== '');
        address = parts.join(', ');
      }
      
      return {
        id: contact.id,
        name: `${contact.firstName} ${contact.lastName}`.trim(),
        phone: contact.phoneNumber || '',
        email: email,
        title: contact.title || '',
        address: address,
        originalContact: contact,
        emailAddressId: contact.emailAddressId,
        addressId: contact.addressId
      };
    });
  }

  loadContacts() {
    if (!this._context.client?.id) {
      this.errorMessage = 'Client context is not available';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this._clientSvc.getContacts(this._context.client.id).subscribe(
      (contacts: Contact[]) => {
        this.tableData = this.mapContactsToTableData(contacts);
        this.isLoading = false;
        this.applyExpand();
      },
      error => {
        console.error('Error loading contacts:', error);
        this.errorMessage = 'Failed to load contacts. Please try again.';
        this.isLoading = false;
        this.toastr.error('Failed to load contacts');
      }
    );
  }

  applyExpand() {
    if (this.currentContactId && this.tableData.length > 0) {
      const tableRow = this.tableData.find(c => c.id === this.currentContactId);
      if (tableRow && tableRow.originalContact) {
        this.isEditMode = true;
        const original = tableRow.originalContact;
        const addr = original.address ? (original.address as any) : {};
        
        this.newContact = {
          firstName: original.firstName || '',
          lastName: original.lastName || '',
          phone: tableRow.phone,
          email: tableRow.email,
          title: tableRow.title,
          streetAddress: addr.address1 || '',
          address2: addr.address2 || '',
          city: addr.city || '',
          state: addr.state || '',
          zip: addr.zip || '',
          officeHours: (original as any).officeHours || ''
        };
        this.form.patchValue(this.newContact);
        this.showNewInline = true;
      } else {
        this.navigateToBase();
      }
    }
  }

  navigateToBase() {
    const baseUrl = this.router.url.split('/contacts')[0] + '/contacts/list';
    this.router.navigateByUrl(baseUrl, { state: { tableData: this.tableData } });
  }

  retry() {
    this.errorMessage = '';
    this.loadContacts();
  }

  private prepareContactForSave(formData: ContactFormData): Contact {
    let contact: Contact;
    
    if (this.currentContactId) {
      const existingData = this.tableData.find(c => c.id === this.currentContactId);
      contact = existingData ? {...existingData.originalContact} : new Contact();
    } else {
      contact = new Contact();
    }
    
    contact.firstName = formData.firstName.trim();
    contact.lastName = formData.lastName.trim();
    contact.phoneNumber = formData.phone;
    contact.title = formData.title;
    
    // Assign generic fields not originally defined in standard contact models
    (contact as any).officeHours = formData.officeHours;
    
    if (this._context.client) {
      contact.client = { id: this._context.client.id } as Partial<Client>;
    }
    
    if (formData.email) {
      if (contact.emailAddress && typeof contact.emailAddress === 'object') {
        (contact.emailAddress as any).email = formData.email;
      } else {
        const emailAddr = new ContactEmailAddress();
        emailAddr.email = formData.email;
        emailAddr.name = 'Default';
        emailAddr.parentType = 'Contact';
        contact.emailAddress = emailAddr;
      }
    } else if (this.currentContactId) {
      if (contact.emailAddress && typeof contact.emailAddress === 'object') {
        (contact.emailAddress as any).email = '';
      }
    }
    
    if (formData.streetAddress || formData.city || formData.state || formData.zip) {
      if (contact.address && typeof contact.address === 'object') {
        const addr = contact.address as any;
        addr.address1 = formData.streetAddress;
        addr.address2 = formData.address2;
        addr.city = formData.city;
        addr.state = formData.state;
        addr.zip = formData.zip;
      } else {
        const addr = new ContactAddress();
        addr.address1 = formData.streetAddress;
        (addr as any).address2 = formData.address2;
        addr.city = formData.city;
        addr.state = formData.state;
        addr.zip = formData.zip;
        addr.name = 'Default';
        addr.parentType = 'Contact';
        contact.address = addr;
      }
    } else if (this.currentContactId) {
      if (contact.address && typeof contact.address === 'object') {
        const addr = contact.address as any;
        addr.address1 = '';
        addr.address2 = '';
        addr.city = '';
        addr.state = '';
        addr.zip = '';
      }
    }
    
    return contact;
  }

  setupNewEditor() {
    this.isEditMode = false;
    this.currentContactId = null;
    this.newContact = {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      title: '',
      streetAddress: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      officeHours: ''
    };
    this.form.reset();
    this.showNewInline = true;
  }

  closeAllEditors() {
    this.showNewInline = false;
    this.isEditMode = false;
    this.currentContactId = null;
    this.form.reset();
  }

  openNewContact() {
    const baseUrl = this.router.url.split('/contacts')[0] + '/contacts/add';
    this.router.navigateByUrl(baseUrl, { state: { tableData: this.tableData } });
  }

  editContact(index: number) {
    const contact = this.tableData[index];
    const baseUrl = this.router.url.split('/contacts')[0] + '/contacts/edit/' + contact.id;
    this.router.navigateByUrl(baseUrl, { state: { tableData: this.tableData } });
  }

  closeNewContact() {
    this.navigateToBase();
  }

  requestDelete(index: number) {
    const contact = this.tableData[index];
    this.pendingDeleteIndex = index;
    
    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Are you sure you want to delete the contact "${contact.name}"?`,
      modalSubDescp: 'This action cannot be undone.',
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL',
    };
  }

  confirmDelete() {
    if (this.pendingDeleteIndex === null) {
      this.closeConfirm();
      return;
    }

    const contact = this.tableData[this.pendingDeleteIndex].originalContact;
    
    this.confirmData = {
      ...this.confirmData,
      replaceYesWith: 'DELETING...',
      yesNoBtn: false
    };

    this._clientSvc.removeContact(contact).subscribe(
      res => {
        this.toastr.success('Contact removed successfully!');
        
        this.tableData = this.tableData.filter(c => c.id !== contact.id);
        
        if (this.currentContactId === contact.id) {
          this.navigateToBase();
        }
        
        this.closeConfirm();
      },
      error => {
        console.error('Error removing contact:', error);
        this.toastr.error('Failed to remove contact');
        this.closeConfirm();
      }
    );
  }

  cancelDelete() {
    this.pendingDeleteIndex = null;
    this.closeConfirm();
  }

  private closeConfirm() {
    this.confirmData = { ...this.confirmData, open: false };
    this.pendingDeleteIndex = null;
  }

  saveNewContact() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      this.toastr.error('Please fill in all required fields correctly');
      return;
    }

    if (!this.newContact.firstName.trim()) {
      this.toastr.error('First name is required');
      return;
    }

    this.isSaving = true;
    const contactToSave = this.prepareContactForSave(this.newContact);
    
    this._clientSvc.saveContact(contactToSave).subscribe(
      (savedContact: Contact) => {
        this._clientSvc.getContacts(this._context.client.id).subscribe({
            next: (contacts: Contact[]) => {
              this.tableData = this.mapContactsToTableData(contacts);
              this.toastr.success(`Contact ${this.isEditMode ? 'updated' : 'added'} successfully!`);
              this.isSaving = false;
              this.closeNewContact(); // Navigates with the correctly updated array
            },
            error: () => {
              this.toastr.success(`Contact ${this.isEditMode ? 'updated' : 'added'} successfully!`);
              this.isSaving = false;
              this.closeNewContact();
            }
        });
      },
      error => {
        console.error('Error saving contact:', error);
        
        let errorMsg = `Failed to ${this.isEditMode ? 'update' : 'add'} contact`;
        if (error.error?.message) {
          errorMsg += `: ${error.error.message}`;
        } else if (error.message) {
          errorMsg += `: ${error.message}`;
        }
        
        this.toastr.error(errorMsg);
        this.isSaving = false;
      }
    );
  }
}